// @flow
import * as React from 'react';
import styled, {
  type StyledComponent,
  ThemeContext,
  type PropsWithTheme,
} from 'styled-components';

import WithTooltip from 'ui/private/with-tooltip';
import type { Theme } from 'ui/styles';
import Flex from 'ui/private/flex';

export type Props = {
  LucideIcon: any,
  overImage?: boolean,
  onClick?: (SyntheticMouseEvent<Node>) => void,
  color?: string,
  style?: { +[string]: string | number },
  size?: string,
  disabled?: boolean,
  tooltip?: React.Node,
  className?: string,
  'data-testid'?: string,
};

type LucideIconWrapperProps = $ReadOnly<{|
  size?: string,
  active?: boolean,
  hover?: boolean,
  overImage?: boolean,
  focus?: boolean,
  children: React.Node,
  // not sure what type is for
  type?: string,
  disabled?: boolean,
|}>;

const backgroundColor = ({
  active,
  hover,
  overImage,
  theme,
  disabled,
}:
  | {|
      active: boolean,
      children: React.Node,
      className?: ?string,
      disabled?: boolean,
      focus?: boolean,
      hover?: boolean,
      overImage?: boolean,
      size?: string,
      style?: { [string]: string | number },
      theme: Theme,
      type?: string,
    |}
  | {|
      active?: boolean,
      children: React.Node,
      className?: ?string,
      disabled: boolean,
      focus?: boolean,
      hover?: boolean,
      overImage?: boolean,
      size?: string,
      style?: { [string]: string | number },
      theme: Theme,
      type?: string,
    |}
  | {|
      active?: boolean,
      children: React.Node,
      className?: ?string,
      disabled?: boolean,
      focus: boolean,
      hover?: boolean,
      overImage?: boolean,
      size?: string,
      style?: { [string]: string | number },
      theme: Theme,
      type?: string,
    |}
  | {|
      active?: boolean,
      children: React.Node,
      className?: ?string,
      disabled?: boolean,
      focus?: boolean,
      hover: boolean,
      overImage?: boolean,
      size?: string,
      style?: { [string]: string | number },
      theme: Theme,
      type?: string,
    |}
  | {|
      active?: boolean,
      children: React.Node,
      className?: ?string,
      disabled?: boolean,
      focus?: boolean,
      hover?: boolean,
      overImage?: boolean,
      size?: string,
      style?: { [string]: string | number },
      theme: Theme,
      type?: string,
    |}): string => {
  const { gray_1: gray1, gray_2: gray2, overlay, white } = theme;
  if (active) {
    return overImage ? 'transparent' : gray2;
  }
  if (hover) {
    return overImage ? overlay : gray1;
  }
  if (disabled) {
    return white;
  }
  return overImage ? 'transparent' : white;
};

const borderColor = ({
  active,
  hover,
  focus,
  overImage,
  theme,
  disabled,
}:
  | {|
      active: boolean,
      children: React.Node,
      className?: ?string,
      disabled?: boolean,
      focus?: boolean,
      hover?: boolean,
      overImage?: boolean,
      size?: string,
      style?: { [string]: string | number },
      theme: Theme,
      type?: string,
    |}
  | {|
      active?: boolean,
      children: React.Node,
      className?: ?string,
      disabled: boolean,
      focus?: boolean,
      hover?: boolean,
      overImage?: boolean,
      size?: string,
      style?: { [string]: string | number },
      theme: Theme,
      type?: string,
    |}
  | {|
      active?: boolean,
      children: React.Node,
      className?: ?string,
      disabled?: boolean,
      focus: boolean,
      hover?: boolean,
      overImage?: boolean,
      size?: string,
      style?: { [string]: string | number },
      theme: Theme,
      type?: string,
    |}
  | {|
      active?: boolean,
      children: React.Node,
      className?: ?string,
      disabled?: boolean,
      focus?: boolean,
      hover: boolean,
      overImage?: boolean,
      size?: string,
      style?: { [string]: string | number },
      theme: Theme,
      type?: string,
    |}
  | {|
      active?: boolean,
      children: React.Node,
      className?: ?string,
      disabled?: boolean,
      focus?: boolean,
      hover?: boolean,
      overImage?: boolean,
      size?: string,
      style?: { [string]: string | number },
      theme: Theme,
      type?: string,
    |}): string => {
  const { brand, gray_1: gray1, white } = theme;
  if (active || focus) {
    return overImage ? white : brand;
  }
  if (hover) {
    return overImage ? 'transparent' : gray1;
  }
  if (disabled) {
    return white;
  }
  return overImage ? 'transparent' : white;
};

export const LucideIconWrapper: StyledComponent<
  LucideIconWrapperProps,
  Theme,
  HTMLButtonElement,
> = styled.button`
  &:hover {
    background-color: ${(
      props: PropsWithTheme<LucideIconWrapperProps, Theme>,
    ) => (props.disabled ? '' : backgroundColor({ ...props, hover: true }))};
    border-color: ${(props: PropsWithTheme<LucideIconWrapperProps, Theme>) =>
      props.disabled ? '' : borderColor({ ...props, hover: true })};
    cursor: ${({ disabled }: PropsWithTheme<LucideIconWrapperProps, Theme>) =>
      disabled ? 'default' : 'pointer'};
  }
  &:active {
    background-color: ${(
      props: PropsWithTheme<LucideIconWrapperProps, Theme>,
    ) => (props.disabled ? '' : backgroundColor({ ...props, active: true }))};
    border-color: ${(props: PropsWithTheme<LucideIconWrapperProps, Theme>) =>
      props.disabled ? '' : borderColor({ ...props, active: true })};
  }
  &:focus {
    background-color: ${(
      props: PropsWithTheme<LucideIconWrapperProps, Theme>,
    ) => (props.disabled ? '' : backgroundColor({ ...props, focus: true }))};
    border-color: ${(props: PropsWithTheme<LucideIconWrapperProps, Theme>) =>
      props.disabled ? '' : borderColor({ ...props, focus: true })};
    outline: 0;
  }
  &:disabled {
    background-color: ${(
      props: PropsWithTheme<LucideIconWrapperProps, Theme>,
    ) => backgroundColor({ ...props, disabled: true })};
    border-color: ${(props: PropsWithTheme<LucideIconWrapperProps, Theme>) =>
      borderColor({ ...props, disabled: true })};
    outline: 0;
    cursor: default;
  }
  align-items: center;
  background-color: ${(props: PropsWithTheme<LucideIconWrapperProps, Theme>) =>
    backgroundColor({ ...props })};
  border-color: ${(props: PropsWithTheme<LucideIconWrapperProps, Theme>) =>
    borderColor({ ...props })};
  border-radius: 50%;
  padding: 4px;
  border-style: solid;
  border-width: 1px;
  cursor: ${({ disabled }: PropsWithTheme<LucideIconWrapperProps, Theme>) =>
    disabled ? 'default' : 'pointer'};
  display: flex;
  justify-content: center;
`;

const LucideIconButton = ({
  LucideIcon,
  onClick,
  overImage,
  color,
  style,
  tooltip,
  size,
  ...rest
}: Props): React.Node => {
  const theme = React.useContext(ThemeContext);

  const LucideIconElement = () => (
    <LucideIconWrapper
      {...rest}
      onClick={onClick}
      type="button"
      overImage={overImage}
    >
      <LucideIcon
        size={size}
        style={{
          ...(style || { ...null }: { +[string]: string | number }),
          color: color || (overImage ? theme.white : theme.black),
        }}
      />
    </LucideIconWrapper>
  );

  return tooltip ? (
    <WithTooltip
      hint={tooltip}
      placement="bottom"
      mouseEnterTimeout={500}
      render={({ targetProps, pointerHandlers }) => (
        <Flex {...pointerHandlers} ref={targetProps.ref}>
          <LucideIconElement />
        </Flex>
      )}
    />
  ) : (
    <LucideIconElement />
  );
};

export default LucideIconButton;
